import {
  pgTable,
  pgEnum,
  uuid,
  varchar,
  text,
  integer,
  timestamp,
  numeric,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { accounts } from "./schema-crm.js";
import {
  baFinancialPeriods,
  baEntityFinancialRecords,
} from "./schema-business-analytics.js";

// ============================================================================
// XERO INTEGRATION MODULE — OAuth + Raw Data + Mappings (SPRINT-24)
// Tables:
//   crm_x_ba_xero_connections      — OAuth token storage per account
//   crm_x_ba_xero_raw_accounts     — Tier 1 raw Xero chart-of-accounts per period
//   crm_x_ba_xero_account_mappings — Tier 2 customisable category mappings per entity
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export const xeroSyncStatusEnum = pgEnum("xero_sync_status", [
  "connected",
  "syncing",
  "sync_error",
  "disconnected",
  "token_expired",
]);

export const xeroAccountTypeEnum = pgEnum("xero_account_type", [
  "BANK",
  "CURRENT",
  "CURRLIAB",
  "DEPRECIATN",
  "DIRECTCOSTS",
  "EQUITY",
  "EXPENSE",
  "FIXED",
  "INVENTORY",
  "LIABILITY",
  "NONCURRENT",
  "OTHERINCOME",
  "OVERHEADS",
  "PREPAYMENT",
  "REVENUE",
  "SALES",
  "TERMLIAB",
  "PAYGLIABILITY",
  "SUPERANNUATIONEXPENSE",
  "SUPERANNUATIONLIABILITY",
  "WABORESPAYABLE",
  "OTHER",
]);

// ============================================================================
// TABLE: crm_x_ba_xero_connections
// Purpose: OAuth token storage per account (one connection per account)
// ============================================================================

export const xeroConnections = pgTable("crm_x_ba_xero_connections", {
  id: uuid("id").primaryKey().defaultRandom(),
  accountId: uuid("account_id")
    .notNull()
    .references(() => accounts.id, { onDelete: "cascade" }),
  xeroTenantId: varchar("xero_tenant_id", { length: 100 }).notNull(),
  xeroOrgName: varchar("xero_org_name", { length: 255 }).notNull(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiresAt: timestamp("token_expires_at", { withTimezone: true }).notNull(),
  scopes: varchar("scopes", { length: 500 }),
  syncStatus: xeroSyncStatusEnum("sync_status").default("connected"),
  lastSyncAt: timestamp("last_sync_at", { withTimezone: true }),
  lastSyncError: text("last_sync_error"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// TABLE: crm_x_ba_xero_raw_accounts
// Purpose: Tier 1 raw Xero chart-of-accounts data per financial period
// ============================================================================

export const xeroRawAccounts = pgTable("crm_x_ba_xero_raw_accounts", {
  id: uuid("id").primaryKey().defaultRandom(),
  financialPeriodId: uuid("financial_period_id")
    .notNull()
    .references(() => baFinancialPeriods.id, { onDelete: "cascade" }),
  xeroAccountId: varchar("xero_account_id", { length: 100 }).notNull(),
  accountCode: varchar("account_code", { length: 10 }),
  accountName: varchar("account_name", { length: 255 }).notNull(),
  accountType: xeroAccountTypeEnum("account_type").notNull(),
  xeroClass: varchar("xero_class", { length: 50 }),
  balance: numeric("balance", { precision: 15, scale: 2 }).notNull().default("0"),
  reportType: varchar("report_type", { length: 20 }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// TABLE: crm_x_ba_xero_account_mappings
// Purpose: Tier 2 customisable category mappings per entity financial record
// ============================================================================

export const xeroAccountMappings = pgTable("crm_x_ba_xero_account_mappings", {
  id: uuid("id").primaryKey().defaultRandom(),
  entityRecordId: uuid("entity_record_id")
    .notNull()
    .references(() => baEntityFinancialRecords.id, { onDelete: "cascade" }),
  xeroAccountCode: varchar("xero_account_code", { length: 10 }).notNull(),
  xeroAccountName: varchar("xero_account_name", { length: 255 }),
  standardCategory: varchar("standard_category", { length: 100 }).notNull(),
  reportSection: varchar("report_section", { length: 20 }).notNull(),
  customCategoryLabel: varchar("custom_category_label", { length: 255 }),
  isOverride: integer("is_override").default(0),
  isTemplate: integer("is_template").default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// crm_x_ba_xero_oauth_states (EDG-1135) — server-side OAuth state nonce store
// Used to validate the `state` query param returned by Xero in the OAuth
// callback. Prevents CSRF (RFC 6749 §10.12): without server-side validation,
// an attacker could craft an authorize URL with a victim's accountId in the
// state and link the attacker's Xero org to the victim's CRM X account.
// ============================================================================

export const xeroOauthStates = pgTable("crm_x_ba_xero_oauth_states", {
  stateNonce: text("state_nonce").primaryKey(),
  accountId: uuid("account_id").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});


// ============================================================================
// ZOD SCHEMAS — xeroConnections
// ============================================================================

export const insertXeroConnectionSchema = createInsertSchema(xeroConnections, {
  accountId: z.string().uuid(),
  xeroTenantId: z.string().min(1).max(100),
  xeroOrgName: z.string().min(1).max(255),
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  tokenExpiresAt: z.string(),
  scopes: z.string().max(500).optional(),
  syncStatus: z
    .enum(["connected", "syncing", "sync_error", "disconnected", "token_expired"])
    .optional(),
  lastSyncAt: z.string().optional(),
  lastSyncError: z.string().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

export const updateXeroConnectionSchema = insertXeroConnectionSchema.partial();

export const selectXeroConnectionSchema = createSelectSchema(xeroConnections);

// ============================================================================
// ZOD SCHEMAS — xeroRawAccounts
// ============================================================================

export const insertXeroRawAccountSchema = createInsertSchema(xeroRawAccounts, {
  financialPeriodId: z.string().uuid(),
  xeroAccountId: z.string().min(1).max(100),
  accountCode: z.string().max(10).optional(),
  accountName: z.string().min(1).max(255),
  accountType: z.enum([
    "BANK",
    "CURRENT",
    "CURRLIAB",
    "DEPRECIATN",
    "DIRECTCOSTS",
    "EQUITY",
    "EXPENSE",
    "FIXED",
    "INVENTORY",
    "LIABILITY",
    "NONCURRENT",
    "OTHERINCOME",
    "OVERHEADS",
    "PREPAYMENT",
    "REVENUE",
    "SALES",
    "TERMLIAB",
    "PAYGLIABILITY",
    "SUPERANNUATIONEXPENSE",
    "SUPERANNUATIONLIABILITY",
    "WABORESPAYABLE",
    "OTHER",
  ]),
  xeroClass: z.string().max(50).optional(),
  balance: z.string().optional(),
  reportType: z.string().max(20).optional(),
}).omit({ id: true, createdAt: true });

// ============================================================================
// ZOD SCHEMAS — xeroAccountMappings
// ============================================================================

export const insertXeroAccountMappingSchema = createInsertSchema(xeroAccountMappings, {
  entityRecordId: z.string().uuid(),
  xeroAccountCode: z.string().min(1).max(10),
  xeroAccountName: z.string().max(255).optional(),
  standardCategory: z.string().min(1).max(100),
  reportSection: z.string().min(1).max(20),
  customCategoryLabel: z.string().max(255).optional(),
  isOverride: z.number().int().min(0).max(1).optional(),
  isTemplate: z.number().int().min(0).max(1).optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

export const updateXeroAccountMappingSchema = insertXeroAccountMappingSchema.partial();

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type XeroConnection = typeof xeroConnections.$inferSelect;
export type InsertXeroConnection = typeof xeroConnections.$inferInsert;
export type XeroRawAccount = typeof xeroRawAccounts.$inferSelect;
export type InsertXeroRawAccount = typeof xeroRawAccounts.$inferInsert;
export type XeroAccountMapping = typeof xeroAccountMappings.$inferSelect;
export type InsertXeroAccountMapping = typeof xeroAccountMappings.$inferInsert;
